import api from '../api';

const CUSTOMER_KEY = 'customer';
const CUSTOMER_ACCESS_TOKEN_KEY = 'customerAccessToken';
const CUSTOMER_REFRESH_TOKEN_KEY = 'customerRefreshToken';

/**
 * Получить профиль из localStorage.
 */
export function getCustomer() {
  try {
    const saved = localStorage.getItem(CUSTOMER_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

/**
 * Сохранить профиль в localStorage.
 */
export function saveCustomer(customer) {
  localStorage.setItem(CUSTOMER_KEY, JSON.stringify(customer));
}

/**
 * Удалить профиль из localStorage.
 */
export function clearCustomer() {
  localStorage.removeItem(CUSTOMER_KEY);
}

export function clearCustomerSession() {
  clearCustomer();
  localStorage.removeItem(CUSTOMER_ACCESS_TOKEN_KEY);
  localStorage.removeItem(CUSTOMER_REFRESH_TOKEN_KEY);
}

export async function restoreCustomer() {
  const savedCustomer = getCustomer();
  const accessToken = localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY);

  if (!savedCustomer || !accessToken) {
    clearCustomerSession();
    return null;
  }

  try {
    const res = await api.get('/api/auth/profile');
    const profile = res.data;
    if (!profile?.phone) throw new Error('Сервер вернул некорректный профиль');

    const customer = {
      id: savedCustomer.id || profile.id || profile.phone,
      phone: profile.phone,
      name: profile.name || '',
      savedPoints: profile.savedPoints || [],
    };
    saveCustomer(customer);

    return {
      customer,
      cart: profile.cart,
      favorites: profile.favorites,
      savedPoints: profile.savedPoints || [],
    };
  } catch (err) {
    if (err.response?.status === 401 || err.response?.status === 403) {
      clearCustomerSession();
    }
    throw err;
  }
}

export async function updateProfile(data) {
  return api.put('/api/auth/profile', data);
}

/**
 * Синхронизировать данные профиля с сервером.
 * Первый аргумент сохранён для совместимости со старыми вызовами.
 * Identity backend получает только из customer JWT.
 */
export async function syncProfile(phoneOrData, data) {
  const profileData = data || phoneOrData;
  if (!profileData || typeof profileData !== 'object') return;
  try {
    await updateProfile(profileData);
  } catch (err) {
    console.error('Profile sync error:', err);
  }
}

/**
 * Запросить код подтверждения.
 */
export async function requestCode(phone) {
  const trimmed = phone.trim();
  if (!trimmed) throw new Error('Введите номер телефона');
  await api.post('/api/auth/request-code', { phone: trimmed });
}

/**
 * Подтвердить код и получить профиль.
 * @returns {object} данные профиля с сервера
 */
export async function verifyCode(phone, code) {
  const trimmedPhone = phone.trim();
  const trimmedCode = code.trim();
  if (!trimmedCode) throw new Error('Введите код из SMS');

  const res = await api.post('/api/auth/verify-code', {
    phone: trimmedPhone,
    code: trimmedCode,
  });

  return saveCustomerSession(res.data);
}

export async function startCustomerCall(phone, continuation) {
  return (await api.post('/api/auth/call', { phone: phone.trim(), ...(continuation ? { challengeId: continuation.challengeId, secret: continuation.secret } : {}) }, { skipAuth: true, skipAuthRefresh: true })).data;
}

export async function checkCustomerCall(challenge) {
  const result = (await api.post('/api/auth/call/status', { challengeId: challenge.challengeId, secret: challenge.secret }, { skipAuth: true, skipAuthRefresh: true })).data;
  return result;
}

export async function loginCustomer(phone, password) {
  return saveCustomerSession((await api.post('/api/auth/login', { phone, password }, { skipAuth: true, skipAuthRefresh: true })).data);
}
export async function completeCustomerPassword(challenge, password) {
  return saveCustomerSession((await api.post('/api/auth/call/complete', { challengeId: challenge.challengeId, secret: challenge.secret, password }, { skipAuth: true, skipAuthRefresh: true })).data);
}

function saveCustomerSession(data) {
  const { customer, accessToken, refreshToken, cart, favorites, savedPoints } = data;
  if (!customer?.phone || !accessToken || !refreshToken) {
    throw new Error('Сервер вернул некорректные данные авторизации');
  }

  const normalizedCustomer = {
    id: customer.id || customer.phone,
    phone: customer.phone,
    name: customer.name || '',
    savedPoints: savedPoints || [],
  };

  localStorage.setItem(CUSTOMER_ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(CUSTOMER_REFRESH_TOKEN_KEY, refreshToken);
  saveCustomer(normalizedCustomer);

  return {
    customer: normalizedCustomer,
    cart,
    favorites,
    savedPoints: savedPoints || [],
  };
}

/**
 * Обновить имя пользователя на сервере.
 */
export async function updateProfileName(phone, name) {
  await updateProfile({ name: name.trim() });
}

/**
 * Полностью выйти: синхронизировать корзину/избранное, очистить localStorage.
 */
export async function logout(phone, cartItems, favItems) {
  if (getCustomer()) {
    await syncProfile({
      cart: JSON.stringify(cartItems),
      favorites: JSON.stringify(favItems),
    }).catch(() => {});
  }
  const refreshToken = localStorage.getItem(CUSTOMER_REFRESH_TOKEN_KEY);
  try {
    if (refreshToken) await api.post('/api/auth/logout', { role: 'customer', refreshToken }, { skipAuthRefresh: true, skipAuth: true });
  } catch (err) {
    // Clear local credentials even if the server cannot be reached.
  } finally {
    clearCustomerSession();
  }
}
